/**
 * Remembers the `utm_*` params a visitor arrived with, so that a signup which
 * happens on a LATER visit is still attributed to the campaign that brought
 * them.
 *
 * Why this exists
 * ---------------
 * Every page that feeds signup read its UTMs straight off the query string:
 *
 *    const utmSource = searchParams.get("utm_source") || '';
 *
 * `fbclid` does not — it is written to localStorage on arrival and read back
 * when the URL has none (see SelfServe/LandingPage.js). So a visitor who
 * clicked an ad, left, and came back a week later still carried their fbclid
 * but arrived with no UTMs, registered with five empty strings, and the whole
 * campaign attribution was lost.
 *
 * Nothing downstream needed changing. FitwithUser already has utm_source,
 * utm_medium, utm_campaign, utm_content, utm_term and fbclid columns;
 * users/views.py stores them on register (first value wins, so a later visit
 * cannot overwrite a known campaign with a blank); and the creator-billing
 * webhook in checkout/views.py copies them onto the Stripe Customer AND
 * Subscription metadata. That chain was only ever starved of input.
 *
 * Four decisions worth knowing
 * ----------------------------
 * 1. THE FIVE ARE STORED AS ONE GROUP, replaced atomically. Merging a fresh
 *    utm_source with a remembered utm_campaign would report a combination that
 *    never existed in any real click, which is worse than reporting nothing.
 *
 * 2. THE URL ALWAYS WINS when it carries any UTM, matching how fbclid behaves
 *    (`urlFbclid || localStorage.getItem('fbclid')`). Arriving from a new
 *    campaign replaces the remembered one; the backend's first-value-wins rule
 *    is what then preserves first-touch on the user record.
 *
 * 3. REMEMBERED VALUES EXPIRE. fbclid has no expiry here, but a UTM kept
 *    forever eventually credits a campaign that ran a year before the signup.
 *    UTM_MAX_AGE_DAYS is deliberately a single named constant — change it in
 *    one place if the attribution window changes.
 *
 * 4. localStorage CAN THROW — Safari private mode, storage full, blocked
 *    cookies. Every access is guarded, because losing attribution is a
 *    reporting problem and throwing here would be a broken sales page.
 */

import { appendFirstTouch } from './firstTouch';

export const UTM_KEYS = [
   'utm_source',
   'utm_medium',
   'utm_campaign',
   'utm_content',
   'utm_term',
];

const STORAGE_KEY = 'utm_params';

/** How long a remembered UTM set is still considered to have brought them in. */
export const UTM_MAX_AGE_DAYS = 90;

const EMPTY = UTM_KEYS.reduce((acc, key) => ({ ...acc, [key]: '' }), {});

/**
 * Accepts react-router's `searchParams`, a raw query string, or nothing (in
 * which case the current URL is used). Returning an empty URLSearchParams on
 * anything unexpected keeps every caller on the no-UTM path rather than
 * throwing.
 */
function toSearchParams(search) {
   if (search && typeof search.get === 'function') return search;
   if (typeof search === 'string') return new URLSearchParams(search);
   try {
      return new URLSearchParams(window.location.search);
   } catch (e) {
      return new URLSearchParams();
   }
}

function readStored() {
   try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;

      // An entry with no timestamp predates this util, or was hand-written.
      // Treated as expired rather than trusted forever.
      const savedAt = Number(parsed.saved_at);
      if (!savedAt || Number.isNaN(savedAt)) return null;
      const ageDays = (Date.now() - savedAt) / 86400000;
      if (ageDays > UTM_MAX_AGE_DAYS || ageDays < 0) return null;

      const out = { ...EMPTY };
      UTM_KEYS.forEach((key) => {
         if (typeof parsed[key] === 'string') out[key] = parsed[key];
      });
      return hasAny(out) ? out : null;
   } catch (e) {
      return null;
   }
}

function hasAny(values) {
   return UTM_KEYS.some((key) => values[key]);
}

/** The UTMs present in a query string. Keys with no value are ''. */
export function utmParamsFromSearch(search) {
   const params = toSearchParams(search);
   const out = { ...EMPTY };
   UTM_KEYS.forEach((key) => {
      const value = params.get(key);
      if (typeof value === 'string' && value.trim()) out[key] = value.trim();
   });
   return out;
}

/**
 * Store the UTM set from this URL, if it has one. Called by getUtmParams, so
 * simply reading the params on any page is enough to remember them.
 *
 * Returns what was stored, or null when the URL carried no UTMs (in which case
 * anything already remembered is left alone — a plain visit to the sales page
 * must not wipe the campaign that brought them in originally).
 */
export function rememberUtmParams(search) {
   const fromUrl = utmParamsFromSearch(search);
   if (!hasAny(fromUrl)) return null;
   try {
      window.localStorage.setItem(
         STORAGE_KEY,
         JSON.stringify({ ...fromUrl, saved_at: Date.now() }),
      );
   } catch (e) {
      // Attribution is best-effort; never let storage break the page.
   }
   return fromUrl;
}

/**
 * The UTMs to attribute this visitor to: the URL's if it has any, otherwise
 * the remembered set, otherwise empty strings.
 *
 * Always returns all five keys so existing call sites that did
 * `searchParams.get("utm_source") || ''` keep the same shape.
 *
 * @param {URLSearchParams|string} [search]
 * @returns {{utm_source: string, utm_medium: string, utm_campaign: string, utm_content: string, utm_term: string}}
 */
export function getUtmParams(search) {
   const fromUrl = utmParamsFromSearch(search);
   if (hasAny(fromUrl)) {
      rememberUtmParams(search);
      return fromUrl;
   }
   return readStored() || { ...EMPTY };
}

/**
 * One UTM value, safe to drop into a URL PATH segment.
 *
 * The SSO signup routes carry UTMs as path segments rather than query params:
 *
 *   /api/social/confirm_profile_passive_influencer/<passive_id>/<utm_source>/...
 *
 * Django matches each with a `<str:...>` converter, which is `[^/]+` — one or
 * more characters, none of them a slash. Two consequences:
 *
 *   * AN EMPTY VALUE DOES NOT MATCH. '' produces '//' in the path and the whole
 *     URL fails to resolve, 404ing the signup. 'null' is the sentinel the
 *     backend already decodes as "absent" (users/views.py:2343 does
 *     `utm_source if utm_source != 'null' else ''`), so that is what an empty
 *     value becomes.
 *   * A VALUE CONTAINING '/' CANNOT SURVIVE either, encoded or not — Django
 *     resolves against the percent-decoded path, so %2F becomes a separator
 *     again. Such a value falls back to the sentinel too: losing one UTM field
 *     beats 404ing the signup.
 *
 * Everything else is percent-encoded, so spaces, '#', '?' and '&' are safe.
 */
export function utmPathSegment(value) {
   const str = typeof value === 'string' ? value.trim() : '';
   if (!str || str.includes('/')) return 'null';
   return encodeURIComponent(str);
}

/**
 * Add the visitor's UTMs and fbclid to a sign-up form, skipping empty ones.
 * The backend stores them on a new account (users/signup_tags.py), which is
 * where Stripe reads them from if the account later starts a creator trial.
 */
export function appendSignupTags(formData, search) {
   const utm = getUtmParams(search);
   UTM_KEYS.forEach((key) => {
      if (utm[key]) formData.append(key, utm[key]);
   });
   let fbclid = toSearchParams(search).get('fbclid');
   try {
      fbclid = fbclid || window.localStorage.getItem('fbclid');
   } catch (e) {
      // storage blocked: the URL value, if any, is all we have
   }
   if (fbclid) formData.append('fbclid', fbclid);
   appendFirstTouch(formData, search);
   return formData;
}

/** Forget the remembered set. For tests and for an explicit opt-out. */
export function clearUtmParams() {
   try {
      window.localStorage.removeItem(STORAGE_KEY);
   } catch (e) {
      // nothing to do
   }
}

export default getUtmParams;
